/**
 * Methodology page — how NCO-2015 and ISCO-08 are used across the app.
 *
 * Public, indexable, and deliberately plain: a person deciding whether to
 * trust the career list should be able to read this in two minutes and then
 * go check the primary sources themselves.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Landmark } from "lucide-react";
import PageHead from "@/components/PageHead";
import WeKitLogo from "@/components/WeKitLogo";
import SharedFooter from "@/components/SharedFooter";
import {
  ISCO08_MAJOR_GROUPS,
  OCCUPATION_STANDARDS_SENTENCE,
  OCCUPATION_STANDARD_SOURCES,
} from "@/lib/occupationStandards";
import OnetAttribution from "@/components/OnetAttribution";

const WHERE_IT_SHOWS = [
  {
    title: "Careers Explorer",
    body: "Every listing carries the basis applied to it — the ISCO-08 major group it sits in — and you can sort the whole corpus by ISCO-08 group.",
    to: "/careers",
    linkLabel: "Open the explorer",
  },
  {
    title: "Career detail",
    body: "The provenance line on a role reads NCO-2015 / ISCO-08 with its group, alongside the evidence behind the rest of the page.",
    to: "/careers",
    linkLabel: "Browse roles",
  },
  {
    title: "Your results",
    body: "The careers matched to you are the same classified roles — no separate, softer list for the report.",
    to: "/assessment/v2/result",
    linkLabel: "See your results",
  },
];

export default function OccupationStandards() {
  return (
    <>
      <PageHead
        title="Occupation Standards: NCO-2015 & ISCO-08 | WeKIT™"
        description="How WeKIT™ classifies all 5,877 careers against NCO-2015 and ISCO-08 — the methodology, the official sources, and where the classification shows up in the app."
        path="/occupation-standards"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: "Occupation Standards: NCO-2015 and ISCO-08",
          description: OCCUPATION_STANDARDS_SENTENCE,
          citation: OCCUPATION_STANDARD_SOURCES.map((s) => s.href),
        }}
      />

      <div className="flex min-h-screen flex-col bg-background">
        <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
          <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
            <Link to="/" className="flex items-center gap-2">
              <WeKitLogo size="sm" />
              <span className="font-display text-lg font-bold text-foreground">WeKIT™</span>
            </Link>
            <Link
              to="/faqs"
              className="inline-flex min-h-11 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to FAQs
            </Link>
          </div>
        </header>

        <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
            <Landmark className="h-3 w-3" aria-hidden="true" /> Methodology
          </span>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Occupation standards
          </h1>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {OCCUPATION_STANDARDS_SENTENCE} That is the difference between a career list you can
            act on and a list somebody invented for a report.
          </p>

          <section className="mt-12" aria-labelledby="sources-heading">
            <h2 id="sources-heading" className="font-display text-2xl font-semibold text-foreground">
              The two standards
            </h2>
            <ul className="mt-4 space-y-4">
              {OCCUPATION_STANDARD_SOURCES.map((s) => (
                <li key={s.id} className="rounded-3xl border border-border/60 bg-card/40 p-5">
                  <h3 className="font-display text-lg font-semibold text-foreground">
                    {s.code} — {s.name}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">{s.custodian}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-3 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {s.hrefLabel}
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12" aria-labelledby="onet-heading">
            <h2 id="onet-heading" className="font-display text-2xl font-semibold text-foreground">
              O*NET Web Services
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Alongside the two classifications, occupational descriptors — tasks, skills, work
              activities and outlook signals — are read from O*NET, the U.S. occupational database.
              O*NET codes crosswalk to ISCO-08, which is what lets an American descriptor sit
              beside an Indian classification without either being reinvented.
            </p>
            <OnetAttribution variant="card" className="mt-4" />
          </section>

          <section className="mt-12" aria-labelledby="how-heading">
            <h2 id="how-heading" className="font-display text-2xl font-semibold text-foreground">
              How we apply them
            </h2>
            <ol className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <li>
                <span className="font-medium text-foreground">One role, one code.</span> Each of the
                5,877 roles in the registry is mapped to an ISCO-08 major group and, where it
                exists, its NCO-2015 equivalent. The mapping is stored with the role, not derived at
                display time.
              </li>
              <li>
                <span className="font-medium text-foreground">Labour market data attaches to the code.</span>{" "}
                Demand, wage bands and skills evidence are hung off the classification, which is
                what lets official statistics line up with the role you are reading about.
              </li>
              <li>
                <span className="font-medium text-foreground">No code, no claim.</span> If a role
                has no recorded group, the listing says the classification is pending instead of
                showing a guess.
              </li>
              <li>
                <span className="font-medium text-foreground">Classification is not scoring.</span>{" "}
                Standards describe what a job is. They never decide how well it fits you — that
                comes from your own responses.
              </li>
            </ol>
          </section>

          <section className="mt-12" aria-labelledby="groups-heading">
            <h2 id="groups-heading" className="font-display text-2xl font-semibold text-foreground">
              The ten ISCO-08 major groups
            </h2>
            <dl className="mt-4 divide-y divide-border/60 overflow-hidden rounded-3xl border border-border/60 bg-card/40">
              {Object.entries(ISCO08_MAJOR_GROUPS).map(([code, name]) => (
                <div key={code} className="flex gap-4 px-5 py-3">
                  <dt className="w-8 shrink-0 font-display text-sm font-bold text-primary">{code}</dt>
                  <dd className="text-sm text-muted-foreground">{name}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mt-12" aria-labelledby="where-heading">
            <h2 id="where-heading" className="font-display text-2xl font-semibold text-foreground">
              Where you'll see it
            </h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {WHERE_IT_SHOWS.map((w) => (
                <li key={w.title} className="rounded-3xl border border-border/60 bg-card/40 p-5">
                  <h3 className="font-display text-base font-semibold text-foreground">{w.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
                  <Link
                    to={w.to}
                    className="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-primary underline underline-offset-2"
                  >
                    {w.linkLabel}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </main>

        <SharedFooter />
      </div>
    </>
  );
}
